import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PortfolioSummary from './components/PortfolioSummary';
import FilterToolbar from './components/FilterToolbar';
import CreditTable from './components/CreditTable';
import BulkActionsBar from './components/BulkActionsBar';

const CreditPortfolio = () => {
  const [selectedCredits, setSelectedCredits] = useState([]);
  const [filters, setFilters] = useState({
    registry: 'all',
    methodology: 'all',
    integrity: 'all',
    ccpStatus: 'all',
    vintage: 'all',
    serialNumber: '',
    projectName: '',
    location: '',
    minQuantity: '',
    maxQuantity: '',
    availableOnly: false,
    tokenized: false
  });
  const [isLoading, setIsLoading] = useState(false);

  // Mock portfolio data
  const portfolioData = {
    totalCredits: 2770000,
    highIntegrityCredits: 1850000,
    ccpApprovedCredits: 2100000,
    availableCredits: 2450000
  };

  // Mock credits data
  const mockCredits = [
    {
      id: 'CR001',
      serialNumber: 'VCS-2023-001-12345-67890',
      registryId: 'VCS-2023-001',
      registry: 'Verra',
      projectName: 'Amazon Rainforest Conservation Project',
      projectLocation: 'Acre, Brazil',
      projectType: 'REDD+',
      projectDescription: `Large-scale forest conservation initiative protecting 50,000 hectares of Amazon rainforest.\nPrevents deforestation and supports local communities through sustainable livelihood programs.\nImplements advanced monitoring using satellite imagery and ground-based sensors.`,
      methodology: 'VM0015',
      methodologyVersion: 'v1.1',
      methodologyCategory: 'Forestry & Land Use',
      methodologyDescription: `Methodology for Avoided Unplanned Deforestation (AUD) that establishes procedures for quantifying GHG emission reductions from activities that reduce deforestation rates below historical levels.\n\nThe methodology applies to project activities that implement measures to reduce unplanned deforestation in areas where deforestation is occurring or is likely to occur without the project activity.`,
      methodologyApprovalDate: '2022-03-15',
      vintage: '2023',
      quantity: 125000,
      status: 'Available',
      integrityLabel: 'High',
      ccpStatus: 'Approved',
      article6Eligible: true,
      tokenized: false,
      lastVerification: '2023-12-15',
      monitoringPeriod: '2022-01-01 to 2022-12-31',
      verificationBody: 'SCS Global Services',
      verificationStandard: 'VCS Standard v4.3',
      issuanceDate: '2023-01-15',
      lastUpdated: '2024-01-10 14:30:00',
      mrvSources: [
        { provider: 'SatelliteVu', type: 'Satellite Imagery', lastUpdate: '2024-01-08' },
        { provider: 'Forest Sensors Inc', type: 'IoT Ground Sensors', lastUpdate: '2024-01-09' }
      ],
      thirdPartyRatings: [
        { provider: 'BeZero Carbon', score: 'AA-', stars: 4, assessment: `High-quality REDD+ project with robust monitoring and strong additionality.\nExcellent community engagement and biodiversity co-benefits.`, lastUpdate: '2023-11-20' },
        { provider: 'Sylvera', score: 'A', stars: 4, assessment: `Strong project fundamentals with reliable satellite monitoring.\nGood permanence risk management and leakage controls.`, lastUpdate: '2023-10-15' }
      ],
      transferHistory: [
        { date: '2023-01-15', type: 'Issuance', from: 'Registry', to: 'Project Developer', quantity: 125000 },
        { date: '2023-02-20', type: 'Transfer', from: 'Project Developer', to: 'Carbon Trader A', quantity: 125000 },
        { date: '2023-06-15', type: 'Transfer', from: 'Carbon Trader A', to: 'Global Carbon Corp', quantity: 125000 }
      ]
    },
    {
      id: 'CR002',
      serialNumber: 'GS-2023-002-54321-09876',
      registryId: 'GS-2023-002',
      registry: 'Gold Standard',
      projectName: 'Solar Power Plant Development',
      projectLocation: 'Rajasthan, India',
      projectType: 'Renewable Energy',
      projectDescription: `100MW solar photovoltaic power plant providing clean electricity to the national grid.\nReplaces fossil fuel-based electricity generation and supports India's renewable energy targets.\nCreates local employment and contributes to energy security.`,
      methodology: 'AMS-I.D',
      methodologyVersion: 'v18.0',
      methodologyCategory: 'Renewable Energy',
      methodologyDescription: `Small-scale methodology for grid connected renewable electricity generation.\nApplies to renewable power generation units that supply electricity to a grid and displace more GHG intensive electricity.\n\nEligible technologies include solar PV, wind, hydro, biomass, and geothermal power generation.`,
      methodologyApprovalDate: '2021-09-10',vintage: '2023',quantity: 85000,status: 'Available',integrityLabel: 'High',ccpStatus: 'Approved',
      article6Eligible: false,
      tokenized: true,
      lastVerification: '2023-11-30',monitoringPeriod: '2022-01-01 to 2022-12-31',
      verificationBody: 'TÜV SÜD',
      verificationStandard: 'Gold Standard for Global Goals',issuanceDate: '2023-02-28',lastUpdated: '2024-01-09 16:45:00',
      mrvSources: [
        { provider: 'Grid Monitoring Systems', type: 'Smart Meters', lastUpdate: '2024-01-09' },
        { provider: 'Weather Analytics', type: 'Meteorological Data', lastUpdate: '2024-01-08' }
      ],
      thirdPartyRatings: [
        { provider: 'BeZero Carbon', score: 'A+', stars: 5, assessment: `Excellent renewable energy project with strong additionality and reliable monitoring.\nHigh-quality Gold Standard certification with significant sustainable development benefits.`, lastUpdate: '2023-12-05' },
        { provider: 'Sylvera', score: 'A', stars: 4, assessment: `Well-managed solar project with good operational performance.\nStrong grid integration and reliable electricity generation data.`, lastUpdate: '2023-11-18' }
      ],
      transferHistory: [
        { date: '2023-02-28', type: 'Issuance', from: 'Registry', to: 'Project Developer', quantity: 85000 },
        { date: '2023-04-10', type: 'Transfer', from: 'Project Developer', to: 'Global Carbon Corp', quantity: 85000 }
      ]
    },
    {
      id: 'CR003',serialNumber: 'ACR-2023-003-11111-22222',registryId: 'ACR-2023-003',registry: 'ACR',projectName: 'Improved Forest Management',projectLocation: 'California, USA',projectType: 'Forestry',
      projectDescription: `Sustainable forest management practices across 25,000 acres of private forestland.\nExtends rotation periods and implements selective harvesting to increase carbon storage.\nEnhances biodiversity and provides sustainable timber revenue.`,
      methodology: 'ACR0001',
      methodologyVersion: 'v1.3',
      methodologyCategory: 'Forestry & Land Use',
      methodologyDescription: `Methodology for Improved Forest Management on Private Lands.\nQuantifies GHG benefits from extending forest rotation periods, reducing harvest intensity, and implementing sustainable management practices.\n\nApplies to private forestlands in temperate regions with established forest management plans.`,
      methodologyApprovalDate: '2020-06-20',vintage: '2022',quantity: 45000,status: 'Available',integrityLabel: 'Medium',ccpStatus: 'Under Review',
      article6Eligible: false,
      tokenized: false,
      lastVerification: '2023-08-15',monitoringPeriod: '2021-01-01 to 2021-12-31',
      verificationBody: 'SCS Global Services',
      verificationStandard: 'ACR Standard v6.0',issuanceDate: '2022-11-20',lastUpdated: '2024-01-08 11:20:00',
      mrvSources: [
        { provider: 'LiDAR Surveys Inc', type: 'Aerial LiDAR', lastUpdate: '2024-01-05' },
        { provider: 'Forest Inventory Co', type: 'Ground Measurements', lastUpdate: '2024-01-07' }
      ],
      thirdPartyRatings: [
        { provider: 'BeZero Carbon', score: 'B+', stars: 3, assessment: `Good forest management project with adequate monitoring systems.\nSome uncertainty in baseline calculations and permanence risk management.`, lastUpdate: '2023-09-12' },
        { provider: 'Sylvera', score: 'B', stars: 3, assessment: `Solid IFM project with room for improvement in monitoring frequency.\nBaseline methodology could be more conservative.`, lastUpdate: '2023-08-28' }
      ],
      transferHistory: [
        { date: '2022-11-20', type: 'Issuance', from: 'Registry', to: 'Forest Owner', quantity: 45000 },
        { date: '2023-03-15', type: 'Transfer', from: 'Forest Owner', to: 'Carbon Aggregator', quantity: 45000 },
        { date: '2023-07-22', type: 'Transfer', from: 'Carbon Aggregator', to: 'Global Carbon Corp', quantity: 45000 }
      ]
    }
  ];

  const [filteredCredits, setFilteredCredits] = useState(mockCredits);

  // Filter credits based on current filters
  useEffect(() => {
    let filtered = mockCredits;

    if (filters?.registry !== 'all') {
      filtered = filtered?.filter(credit => 
        credit?.registry?.toLowerCase()?.includes(filters?.registry?.toLowerCase())
      );
    }

    if (filters?.methodology !== 'all') {
      filtered = filtered?.filter(credit => 
        credit?.methodologyCategory?.toLowerCase()?.includes(filters?.methodology?.toLowerCase())
      );
    }

    if (filters?.integrity !== 'all') {
      filtered = filtered?.filter(credit => 
        credit?.integrityLabel?.toLowerCase() === filters?.integrity?.toLowerCase()
      );
    }

    if (filters?.ccpStatus !== 'all') {
      filtered = filtered?.filter(credit => 
        credit?.ccpStatus?.toLowerCase()?.includes(filters?.ccpStatus?.toLowerCase())
      );
    }

    if (filters?.vintage !== 'all') {
      filtered = filtered?.filter(credit => 
        credit?.vintage === filters?.vintage
      );
    }

    if (filters?.serialNumber) {
      filtered = filtered?.filter(credit => 
        credit?.serialNumber?.toLowerCase()?.includes(filters?.serialNumber?.toLowerCase())
      );
    }

    if (filters?.projectName) {
      filtered = filtered?.filter(credit => 
        credit?.projectName?.toLowerCase()?.includes(filters?.projectName?.toLowerCase())
      );
    }

    if (filters?.location) {
      filtered = filtered?.filter(credit => 
        credit?.projectLocation?.toLowerCase()?.includes(filters?.location?.toLowerCase())
      );
    }

    if (filters?.minQuantity) {
      filtered = filtered?.filter(credit => 
        credit?.quantity >= parseInt(filters?.minQuantity)
      );
    }

    if (filters?.maxQuantity) {
      filtered = filtered?.filter(credit => 
        credit?.quantity <= parseInt(filters?.maxQuantity)
      );
    }

    if (filters?.availableOnly) {
      filtered = filtered?.filter(credit => 
        credit?.status === 'Available'
      );
    }

    if (filters?.tokenized) {
      filtered = filtered?.filter(credit => 
        credit?.tokenized === true
      );
    }

    setFilteredCredits(filtered);
  }, [filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      registry: 'all',
      methodology: 'all',
      integrity: 'all',
      ccpStatus: 'all',
      vintage: 'all',
      serialNumber: '',
      projectName: '',
      location: '',
      minQuantity: '',
      maxQuantity: '',
      availableOnly: false,
      tokenized: false
    });
  };

  const handleBulkAction = async (action, creditIds) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log(`Executing ${action} on credits:`, creditIds);
    
    // Handle different actions
    switch (action) {
      case 'transfer':
        alert(`Transfer initiated for ${creditIds?.length} credits`);
        break;
      case 'retire':
        alert(`Retirement process started for ${creditIds?.length} credits`);
        break;
      case 'create-claim': handleNavigate('/claims-builder');
        break;
      case 'generate-dossier':
        alert(`Dossier generation started for ${creditIds?.length} credits`);
        break;
      case 'export':
        alert(`Exporting data for ${creditIds?.length} credits`);
        break;
      case 'tag':
        alert(`Tag management opened for ${creditIds?.length} credits`);
        break;
      default:
        break;
    }
    
    setIsLoading(false);
  };

  const handleNavigate = (path) => {
    window.location.href = path;
  };

  const handleClearSelection = () => {
    setSelectedCredits([]);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Icon name="Briefcase" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-card-foreground">Credit Portfolio</h1>
                <p className="text-muted-foreground">
                  Manage and verify carbon credits across multiple registries with comprehensive integrity tracking
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                variant="outline" 
                iconName="RefreshCw"
                onClick={() => window.location?.reload()}
              >
                Sync Registries
              </Button>
              <Button 
                variant="default" 
                iconName="Plus"
                onClick={() => handleNavigate('/claims-builder')}
              >
                Create Claim
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Portfolio Summary */}
        <PortfolioSummary 
          portfolioData={portfolioData}
          onNavigate={handleNavigate}
        />

        {/* Filter Toolbar */}
        <FilterToolbar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          resultCount={filteredCredits?.length}
          onClearFilters={handleClearFilters}
        />

        {/* Credits Table */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-card-foreground">
              Credits ({filteredCredits?.length?.toLocaleString()})
            </h2>
            
            {selectedCredits?.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {selectedCredits?.length} credit{selectedCredits?.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          <CreditTable
            credits={filteredCredits}
            selectedCredits={selectedCredits}
            onSelectionChange={setSelectedCredits}
            onBulkAction={handleBulkAction}
          />
        </div>

        {/* Empty State */}
        {filteredCredits?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-card-foreground mb-2">No credits found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your filters or search criteria to find credits.
            </p>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCredits={selectedCredits}
        onBulkAction={handleBulkAction}
        onClearSelection={handleClearSelection}
      />
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-card-foreground">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditPortfolio;